import React, { createContext, useContext, useEffect, useState } from 'react';
import { trackEvent, trackPageView, trackUserAction, getAnalyticsStatus } from './analytics';

// Define the context type
interface AnalyticsContextType {
  trackEvent: (eventName: string, eventParams?: Record<string, any>) => void;
  trackUserAction: (actionName: string, actionParams?: Record<string, any>) => void;
  sessionId: string;
  analyticsStatus: any;
}

// Create context with default values
const AnalyticsContext = createContext<AnalyticsContextType>({
  trackEvent: () => {},
  trackUserAction: () => {},
  sessionId: '',
  analyticsStatus: null
});

// Generate a unique session ID
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sessionId, setSessionId] = useState<string>('');
  const [analyticsStatus, setAnalyticsStatus] = useState<any>(null);

  // Initialize session on component mount
  useEffect(() => {
    // Generate a unique session ID
    const newSessionId = generateSessionId();
    setSessionId(newSessionId);

    // Get analytics status
    const status = getAnalyticsStatus();
    setAnalyticsStatus(status);

    // Track initial page load
    trackPageView('Initial Page Load');
    trackEvent('session_start', { 
      session_id: newSessionId,
      timestamp: new Date().toISOString(),
      referrer: document.referrer,
      user_agent: navigator.userAgent
    });

    // Add listener for when user is about to leave the page
    const handleBeforeUnload = () => {
      trackEvent('session_end', {
        session_id: newSessionId,
        duration_seconds: (Date.now() - parseInt(newSessionId.split('_')[1])) / 1000,
        timestamp: new Date().toISOString()
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Track route changes (if using a router)
  useEffect(() => {
    const handleRouteChange = () => {
      trackPageView(window.location.pathname);
    };

    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  // Function to track custom events
  const trackCustomEvent = (eventName: string, eventParams?: Record<string, any>) => {
    trackEvent(eventName, {
      ...eventParams,
      session_id: sessionId
    });
  };

  // Function to track user actions (clicks, interactions, etc.)
  const trackCustomUserAction = (actionName: string, actionParams?: Record<string, any>) => {
    trackUserAction(actionName, {
      ...actionParams,
      session_id: sessionId,
      timestamp: new Date().toISOString()
    });
  };

  // Context value
  const value = {
    trackEvent: trackCustomEvent,
    trackUserAction: trackCustomUserAction,
    sessionId,
    analyticsStatus
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Custom hook to use analytics context
export const useAnalytics = () => {
  return useContext(AnalyticsContext);
};

/**
 * Example usage:
 * 
 * // Wrap your app in the provider
 * <AnalyticsProvider>
 *   <App />
 * </AnalyticsProvider>
 * 
 * // In a component
 * const { trackEvent, trackUserAction } = useAnalytics();
 * 
 * // Track a custom event
 * trackEvent('feature_used', { feature_name: 'chat', feature_value: 'sent_message' });
 * 
 * // Track a user action
 * trackUserAction('button_click', { button_name: 'submit', page: 'checkout' });
 */ 